import { ElementTypes, type Shot, type TemplateElement } from '@bynder-studio/render-core';

type ElementTree = TemplateElement[] | Record<string | number, TemplateElement> | null | undefined;

type SceneElementState = {
    hidden?: boolean;
    locked?: boolean;
    allowToggleVisibility?: boolean;
    onlyAllowOneSelection?: boolean;
    activeDropdownElementId?: number | null;
};

type FrameRange = {
    fromFrame: number;
    toFrame: number;
};

type BuildSceneRowsOptions = {
    editableTypes: Set<ElementTypes>;
    elementStatesById?: Record<number, SceneElementState>;
    isDynamicElementSelectionEnabled?: boolean;
    depth?: number;
    allowedIds?: Set<number>;
    disabledIds?: Set<number>;
    forceVisibleIds?: Set<number>;
    audioLast?: boolean;
};

export type SceneRow =
    | {
          kind: 'element';
          id: number;
          depth: number;
          element: TemplateElement;
          forceRender: boolean;
      }
    | {
          kind: 'des-group';
          id: number;
          depth: number;
          element: TemplateElement;
          selectedChild: TemplateElement | null;
          children: SceneRow[];
      };

const IMAGE_EDITABLE_TYPES = new Set([ElementTypes.IMAGE, ElementTypes.TEXT, ElementTypes.SHAPE]);
const VIDEO_EDITABLE_TYPES = new Set([
    ElementTypes.IMAGE,
    ElementTypes.VIDEO,
    ElementTypes.TEXT,
    ElementTypes.SHAPE,
    ElementTypes.AUDIO,
]);

const getElements = (elements: ElementTree): TemplateElement[] => {
    if (!elements) {
        return [];
    }

    return Array.isArray(elements) ? elements : Object.values(elements);
};

const getChildren = (element: TemplateElement): TemplateElement[] => getElements(element.children as ElementTree);

const getElementState = (
    element: TemplateElement,
    elementStatesById?: Record<number, SceneElementState>,
): SceneElementState => ({
    ...(element.properties || {}),
    ...(elementStatesById?.[element.id] || {}),
});

const isAudioElement = (element: TemplateElement) => element.type === ElementTypes.AUDIO;

export const compareByRenderOrder = (a, b) => (b.properties?.renderOrder || 0) - (a.properties?.renderOrder || 0);

const sortSceneElements = (elements: TemplateElement[], audioLast = false) => {
    const sortedElements = [...elements].sort(compareByRenderOrder);

    if (!audioLast) {
        return sortedElements;
    }

    return sortedElements.sort((left, right) => {
        if (isAudioElement(left) === isAudioElement(right)) {
            return 0;
        }

        return isAudioElement(left) ? 1 : -1;
    });
};

const hasAllowedElementInTree = (element: TemplateElement, allowedIds?: Set<number>): boolean => {
    if (!allowedIds) {
        return true;
    }

    const children = getChildren(element);

    if (!children.length) {
        return allowedIds.has(Number(element.id));
    }

    return children.some((child) => hasAllowedElementInTree(child, allowedIds));
};

const shouldIncludeElement = (element: TemplateElement, options: BuildSceneRowsOptions) => {
    const { editableTypes, allowedIds, disabledIds, forceVisibleIds } = options;
    const state = getElementState(element, options.elementStatesById);
    const elementId = Number(element.id);
    const isForceVisible = forceVisibleIds?.has(elementId) ?? false;

    if (!editableTypes.has(element.type)) {
        return false;
    }

    if (!isForceVisible && allowedIds && !allowedIds.has(elementId)) {
        return false;
    }

    if (disabledIds?.has(elementId)) {
        return false;
    }

    if (!isForceVisible && state.hidden && !state.allowToggleVisibility) {
        return false;
    }

    return true;
};

const isDesGroup = (element: TemplateElement, options: BuildSceneRowsOptions) => {
    if (!options.isDynamicElementSelectionEnabled || element.type !== ElementTypes.GROUP) {
        return false;
    }

    const state = getElementState(element, options.elementStatesById);

    return Boolean(state.onlyAllowOneSelection);
};

const getFallbackChild = (children: TemplateElement[], allowedIds?: Set<number>) => {
    if (!allowedIds) {
        return children[0] || null;
    }

    return children.find((child) => hasAllowedElementInTree(child, allowedIds)) || null;
};

const getSelectedDesChild = (element: TemplateElement, options: BuildSceneRowsOptions) => {
    const sortedChildren = sortSceneElements(getChildren(element));
    const state = getElementState(element, options.elementStatesById);
    const activeChildId =
        state.activeDropdownElementId ??
        element.properties?.activeDropdownElementId ??
        getFallbackChild(sortedChildren)?.id;

    return (
        sortedChildren.find((child) => Number(child.id) === Number(activeChildId)) ||
        getFallbackChild(sortedChildren, options.allowedIds)
    );
};

const hasAllowedScenePath = (element: TemplateElement, options: BuildSceneRowsOptions): boolean => {
    if (!options.allowedIds) {
        return true;
    }

    const children = getChildren(element);

    if (!children.length) {
        return options.allowedIds.has(Number(element.id));
    }

    if (isDesGroup(element, options)) {
        const selectedChild = getSelectedDesChild(element, options);

        return selectedChild ? hasAllowedScenePath(selectedChild, options) : false;
    }

    return children.some((child) => hasAllowedScenePath(child, options));
};

const hasSelectableDesOptionInScene = (element: TemplateElement, options: BuildSceneRowsOptions): boolean =>
    sortSceneElements(getChildren(element)).some((child) => hasAllowedScenePath(child, options));

const buildSceneRows = (elements: ElementTree, options: BuildSceneRowsOptions): SceneRow[] => {
    const { depth = 0 } = options;

    return sortSceneElements(getElements(elements), options.audioLast).reduce<SceneRow[]>((rows, element) => {
        const children = getChildren(element);

        if (isDesGroup(element, options)) {
            const state = getElementState(element, options.elementStatesById);

            if (state.locked) {
                return rows;
            }

            const selectedChild = getSelectedDesChild(element, options);

            if (!selectedChild && !hasSelectableDesOptionInScene(element, options)) {
                return rows;
            }

            const childRows = hasAllowedScenePath(selectedChild, options)
                ? buildSceneRows([selectedChild], {
                      ...options,
                      depth: depth + 1,
                      forceVisibleIds: new Set([...(options.forceVisibleIds || []), Number(selectedChild.id)]),
                      audioLast: false,
                  })
                : [];

            rows.push({
                kind: 'des-group',
                id: Number(element.id),
                depth,
                element,
                selectedChild,
                children: childRows,
            });

            return rows;
        }

        if (children.length) {
            rows.push(
                ...buildSceneRows(children, {
                    ...options,
                    depth,
                    audioLast: false,
                }),
            );

            return rows;
        }

        if (!shouldIncludeElement(element, options)) {
            return rows;
        }

        rows.push({
            kind: 'element',
            id: Number(element.id),
            depth,
            element,
            forceRender: options.forceVisibleIds?.has(Number(element.id)) ?? false,
        });

        return rows;
    }, []);
};

const getShotRange = (shot: Shot): FrameRange => ({
    fromFrame: shot.startFrame,
    toFrame: shot.startFrame + shot.duration - 1,
});

const isBlockedByColorsLack = (element: TemplateElement) => {
    if (element.type === ElementTypes.BACKGROUND_COLOR) {
        return !element.properties.brandColors.length;
    }

    return false;
};

const elementIsInFrameRange = (element: TemplateElement, range: FrameRange) => {
    const startFrame = element.properties?.startFrame ?? 0;

    return startFrame >= range.fromFrame && startFrame < range.toFrame;
};

export const getSceneElementStateMap = (elements: any[] = []): Record<number, SceneElementState> =>
    elements.reduce<Record<number, SceneElementState>>((acc, element) => {
        acc[Number(element.id)] = {
            hidden: element.hidden,
            locked: element.locked,
            allowToggleVisibility: element.allowToggleVisibility,
            onlyAllowOneSelection: element.onlyAllowOneSelection,
            activeDropdownElementId: element.activeDropdownElementId,
        };

        return acc;
    }, {});

export const getElementIdsForSceneRange = (
    elements: ElementTree,
    range: FrameRange,
    ids: Set<number> = new Set<number>(),
) => {
    getElements(elements).forEach((element) => {
        const children = getChildren(element);

        if (children.length) {
            getElementIdsForSceneRange(children, range, ids);
            return;
        }

        if (elementIsInFrameRange(element, range)) {
            ids.add(Number(element.id));
        }
    });

    return ids;
};

export const getShotDisabledElementIds = ({
    elements,
    currentShot,
    previousShot,
}: {
    elements: ElementTree;
    currentShot?: Shot;
    previousShot?: Shot;
}) => {
    const disabledIds = new Set<number>();

    if (!currentShot?.elements?.length || !previousShot) {
        return disabledIds;
    }

    const previousElementsById = new Map(previousShot.elements.map((element: any) => [Number(element.id), element]));
    const previousVisibleIds = getElementIdsForSceneRange(elements, getShotRange(previousShot));

    currentShot.elements.forEach((element: any) => {
        const previousElement = previousElementsById.get(Number(element.id));

        if (!element.disabled || !previousElement) {
            return;
        }

        if (previousElement.disabled || previousVisibleIds.has(Number(element.id))) {
            disabledIds.add(Number(element.id));
        }
    });

    return disabledIds;
};

export const getImageDesignSceneRows = ({
    elements,
    elementStatesById,
    isDynamicElementSelectionEnabled,
}: {
    elements: ElementTree;
    elementStatesById?: Record<number, SceneElementState>;
    isDynamicElementSelectionEnabled?: boolean;
}) =>
    buildSceneRows(elements, {
        editableTypes: IMAGE_EDITABLE_TYPES,
        elementStatesById,
        isDynamicElementSelectionEnabled,
    });

export const getVideoDesignSceneRows = ({
    elements,
    elementStatesById,
    isDynamicElementSelectionEnabled,
    allowedIds,
    disabledIds,
}: {
    elements: ElementTree;
    elementStatesById?: Record<number, SceneElementState>;
    isDynamicElementSelectionEnabled?: boolean;
    allowedIds?: Set<number>;
    disabledIds?: Set<number>;
}) =>
    buildSceneRows(elements, {
        editableTypes: VIDEO_EDITABLE_TYPES,
        elementStatesById,
        isDynamicElementSelectionEnabled,
        allowedIds,
        disabledIds,
        audioLast: true,
    });

export const getGlobalEditableElements = (elements: Record<string | number, TemplateElement>): TemplateElement[] =>
    Object.values(elements).filter(
        (element) =>
            // NOTE: We can check global elements for additional colors HERE because they
            // can't be shown/hidden and don't need a header like not-global elements
            [ElementTypes.BACKGROUND_COLOR].includes(element.type) && !isBlockedByColorsLack(element),
        // will be included further
        // && !element.properties.locked
    );
